import { MessageSquareQuoteIcon } from 'lucide-react';

import { RelativeTime } from '@/shared/components/date';
import type { SearchResult, SearchResultHighlights } from '@/shared/registry/types';

/**
 * A comment in the global search results (the content of the search page's link row): "Comment on «the task»", the author and
 * the time on the first line, the comment's words with the matches marked below. The owning module (tasks, feed, wiki) says what
 * the comment is on (`heading`); the row links to that entity.
 */
export function CommentSearchResult({
  result,
  highlights,
  heading,
}: {
  result: SearchResult;
  highlights?: SearchResultHighlights;
  heading: string;
}) {
  const author = result.meta?.authorName;
  const snippet = highlights?.snippet ?? result.snippet;
  return (
    <>
      <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg border bg-background text-brand-text shadow-xs">
        <MessageSquareQuoteIcon className="size-[1.125rem]" strokeWidth={1.8} aria-hidden />
      </span>
      <span className="min-w-0 flex-1" data-testid="comment-search-result">
        <span className="flex min-w-0 flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
          <span className="truncate font-medium text-foreground/70" data-testid="comment-search-heading">
            {heading}
          </span>
          {author && (
            <>
              <span aria-hidden>·</span>
              <span className="truncate">{author}</span>
            </>
          )}
          {result.updatedAt && (
            <>
              <span aria-hidden>·</span>
              <RelativeTime date={result.updatedAt} />
            </>
          )}
        </span>
        <span className="mt-0.5 line-clamp-2 block text-[0.9375rem] break-words text-foreground">
          {highlights?.title ?? result.title}
        </span>
        {snippet && (
          <span className="mt-0.5 line-clamp-2 block text-sm break-words text-muted-foreground">
            {snippet}
          </span>
        )}
      </span>
    </>
  );
}
